import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger, TabsContent, Badge, Button } from '@/components/ui';
import { Sparkles } from 'lucide-react';
import { SaltField } from './salt-field';
import { BuildTab } from './build-tab';
import { VerifyTab } from './verify-tab';
import { TeaserNotice } from './teaser-notice';
import { hasDemoData } from './demo';
import { PENDING_FORMAT } from './format-definition';
import type { PaymentLinkFormatDefinition } from './format-definition';
import { resolvePayBaseUrl } from './stage-map';

/**
 * Payment-Link Link-Tester — Werkzeug-Shell (Spec 501, Phase 5).
 *
 * Einstiegs-agnostisch/props-getrieben: nimmt die Format-Definition als Prop
 * (Default `PENDING_FORMAT` = Teaser). Spaetere Mounts (Inc-2b-Service-Tab,
 * Devportal-Track) stecken die echte Format-Definition ein, ohne die Shell zu
 * aendern. Zwei Tabs mit GEMEINSAMEM Salt-State — dieser lebt ausschliesslich
 * in-memory (`useState`), nie Query-Cache/Router/Storage (Spec §Scope 5).
 */
export interface LinkTesterProps {
  /** Format-Definition. Default: Teaser (`PENDING_FORMAT`). */
  format?: PaymentLinkFormatDefinition;
  /** Optionaler vorausgewaehlter Service (Mount-Kontext). */
  initialServiceId?: string;
  /** Panel-Host fuer die Stage-Map (Default: `window.location.hostname`). */
  hostname?: string;
  /**
   * Spec 516 D-8: Wizard-Pfad — der bereits geladene Service wird durchgereicht;
   * der tester-eigene Service-Detail-Join unterbleibt dann komplett (kein
   * Zweit-Fetch). Fehlt die Prop (Standalone), joint der Tester selbst.
   */
  presetService?: {
    serviceId: string;
    merchantId: string;
    publicBaseUrl?: string;
  };
}

export function LinkTester({
  format = PENDING_FORMAT,
  initialServiceId,
  hostname,
  presetService,
}: LinkTesterProps) {
  const { t } = useTranslation('payment-link');
  // Gemeinsamer Salt — NUR in-memory. Kein Persist-/Cache-/Router-Pfad.
  const [salt, setSalt] = useState('');
  // `true` = der aktuelle Salt stammt aus dem Demo-Testvektor (sichtbar markiert).
  const [isDemoSalt, setIsDemoSalt] = useState(false);
  // Demo-Auslöser: jeder Klick erhöht die Nonce → BuildTab wendet den Testvektor
  // an. Der Demo-Salt lebt wie jeder Salt nur im in-memory-State.
  const [demoNonce, setDemoNonce] = useState(0);

  // Manuelle Salt-Eingabe beendet den Demo-Zustand.
  function changeSalt(value: string) {
    setSalt(value);
    setIsDemoSalt(false);
  }

  function applyDemo() {
    if (!hasDemoData(format) || !format.testVector) {
      return;
    }
    setSalt(format.testVector.salt);
    setIsDemoSalt(true);
    setDemoNonce((n) => n + 1);
  }

  const host =
    hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '');
  const baseEntry = resolvePayBaseUrl(host);

  return (
    <div className="space-y-4">
      {!format.available && (
        <div className="space-y-3">
          <Badge variant="outline">{t('tester.teaser.badge')}</Badge>
          <TeaserNotice />
        </div>
      )}

      {/* Demo nutzt AUSSCHLIESSLICH den Testvektor der Format-Definition (Spec 503):
          füllt Salt + Parameter aus `format.testVector` (der offizielle Facade-
          Testvektor). Im Teaser (`PENDING_FORMAT`, kein Testvektor) deaktiviert. */}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasDemoData(format)}
          onClick={applyDemo}
          title={
            hasDemoData(format) ? undefined : t('tester.demo.disabledHint')
          }
        >
          <Sparkles className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('tester.demo.button')}
        </Button>
      </div>

      <SaltField value={salt} onChange={changeSalt} demoNotice={isDemoSalt} />

      <Tabs defaultValue="build">
        <TabsList>
          <TabsTrigger value="build">{t('tester.tabs.build')}</TabsTrigger>
          <TabsTrigger value="verify">{t('tester.tabs.verify')}</TabsTrigger>
        </TabsList>

        <TabsContent value="build" className="pt-4">
          <BuildTab
            format={format}
            baseEntry={baseEntry}
            salt={salt}
            initialServiceId={initialServiceId ?? presetService?.serviceId}
            demoNonce={demoNonce}
            presetService={presetService}
          />
        </TabsContent>

        <TabsContent value="verify" className="pt-4">
          <VerifyTab format={format} baseEntry={baseEntry} salt={salt} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
